// BL-42 — an identity document you can actually read.
//
// The review dialog capped the image at 384px tall inside a small dialog, which
// is smaller than the ID itself: a reviewer could not read a licence number or
// check a date, which is the entire job. Clicking the image now opens it on a
// near-full-screen surface with zoom and rotate (phone photos of IDs arrive
// sideways more often than not).
//
// The signed URL still expires in about a minute and every open is still
// audited — nothing here changes how the bytes are reached, only how big they
// are on screen.

import { Dialog, DialogContent, DialogTitle } from "@bednbody/ui/components/dialog";
import { Button } from "@bednbody/ui/components/button";
import {
  ArrowClockwiseIcon,
  ArrowCounterClockwiseIcon,
  MagnifyingGlassMinusIcon,
  MagnifyingGlassPlusIcon,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";

const ZOOM_STEP = 0.25;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4;

export function DocumentViewer({
  src,
  alt,
  title,
  open,
  onOpenChange,
}: {
  src: string | null;
  alt: string;
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);

  // Every open starts square and unzoomed, so the last document's fiddling is
  // not inherited by the next one.
  useEffect(() => {
    if (open) {
      setZoom(1);
      setRotation(0);
    }
  }, [open]);

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="flex h-[92vh] max-w-[min(96vw,1400px)] flex-col gap-0 overflow-hidden rounded-2xl p-0 sm:max-w-[min(96vw,1400px)]">
        <div className="flex items-center justify-between gap-3 border-b border-border bg-card px-5 py-3 pr-14">
          <DialogTitle className="truncate text-base font-semibold">{title}</DialogTitle>
          <div className="flex items-center gap-1">
            <Button
              aria-label="Zoom out"
              disabled={zoom <= MIN_ZOOM}
              onClick={() => setZoom((current) => Math.max(MIN_ZOOM, current - ZOOM_STEP))}
              shape="soft"
              size="icon-sm"
              variant="outline"
            >
              <MagnifyingGlassMinusIcon />
            </Button>
            <span className="w-14 text-center text-sm tabular-nums text-muted-foreground">
              {Math.round(zoom * 100)}%
            </span>
            <Button
              aria-label="Zoom in"
              disabled={zoom >= MAX_ZOOM}
              onClick={() => setZoom((current) => Math.min(MAX_ZOOM, current + ZOOM_STEP))}
              shape="soft"
              size="icon-sm"
              variant="outline"
            >
              <MagnifyingGlassPlusIcon />
            </Button>
            <Button
              aria-label="Rotate left"
              className="ml-2"
              onClick={() => setRotation((current) => (current + 270) % 360)}
              shape="soft"
              size="icon-sm"
              variant="outline"
            >
              <ArrowCounterClockwiseIcon />
            </Button>
            <Button
              aria-label="Rotate right"
              onClick={() => setRotation((current) => (current + 90) % 360)}
              shape="soft"
              size="icon-sm"
              variant="outline"
            >
              <ArrowClockwiseIcon />
            </Button>
          </div>
        </div>

        {/* The pane scrolls, so a zoomed document can be panned rather than
            clipped. A plain dark backdrop: an ID photographed on a desk reads
            better against it than against the page. */}
        <div className="min-h-0 flex-1 overflow-auto bg-foreground/90 p-4">
          {src ? (
            <div className="flex min-h-full min-w-full items-center justify-center">
              <img
                alt={alt}
                className="max-w-none origin-center transition-transform duration-150"
                src={src}
                style={{ transform: `scale(${zoom}) rotate(${rotation}deg)` }}
              />
            </div>
          ) : (
            <p className="py-20 text-center text-sm text-background">
              The link expired. Close this and open the document again.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
